import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { WeekCountRow, WeekCounts } from '../api/types';
import { useApp } from '../state/AppContext';
import { useWeekAndRegion } from '../state/useFilters';
import { ErrorBanner, SortableHeader, formatDate, formatLongDate, useTableSort } from '../components/ui';
import { RegionSelect, WeekSelect } from '../components/Filters';

type Show = 'all' | 'todo' | 'done';

/** How "this week" sorts: not counted first, then late, imported, and on time. */
const STATUS_RANK: Record<WeekCountRow['status'], number> = { missing: 0, late: 1, imported: 2, onTime: 3 };

/**
 * Who has counted this week, and the way in to counting: one row per location,
 * grouped, with the ones still to count at the top of each group.
 */
export function Counts() {
  const { reference, locationsFor, regionName } = useApp();
  const { week, region, setWeek, setRegion } = useWeekAndRegion();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [show, setShow] = useState<Show>('all');

  const counts = useQuery({
    queryKey: ['counts', week, region],
    queryFn: () => api.get<WeekCounts>('/api/counts', { weekEnding: week, region }),
  });

  const isOpen = counts.data?.isOpen ?? true;
  const all = useMemo(() => counts.data?.rows ?? [], [counts.data]);
  const done = all.filter((r) => r.status !== 'missing').length;

  const { rows: sorted, sort, onSort } = useTableSort(all, {
    location: (r) => r.code,
    region: (r) => regionName(r.regionCode),
    type: (r) => r.type,
    status: (r) => `${STATUS_RANK[r.status]}|${r.latest?.businessDate ?? ''}`,
  });

  // Groups in Setup's order. Inside each, not counted first, then by name —
  // until a heading is clicked, which sorts the rows inside every group instead.
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out: { name: string; total: number; done: number; rows: WeekCountRow[] }[] = [];
    for (const r of all) {
      let group = out.find((g) => g.name === r.group);
      if (!group) { group = { name: r.group, total: 0, done: 0, rows: [] }; out.push(group); }
      group.total += 1;
      if (r.status !== 'missing') group.done += 1;
    }
    for (const r of sorted) {
      const matchesShow = show === 'all' || (show === 'todo' ? r.status === 'missing' : r.status !== 'missing');
      const matchesSearch = !q || r.code.toLowerCase().includes(q) || r.type.toLowerCase().includes(q);
      if (matchesShow && matchesSearch) out.find((g) => g.name === r.group)?.rows.push(r);
    }
    if (!sort) {
      for (const g of out) {
        g.rows.sort((a, b) =>
          Number(b.status === 'missing') - Number(a.status === 'missing')
          || a.code.localeCompare(b.code, undefined, { numeric: true }));
      }
    }
    return out.filter((g) => g.rows.length > 0);
  }, [all, sorted, sort, search, show]);

  // A daily location's count is for a day, and never a day still to come.
  const periodFor = (r: WeekCountRow) => (r.frequency === 'daily' && reference.today < week ? reference.today : week);
  const toCount = (r: WeekCountRow) => `/counts/${r.locationId}?periodEnd=${periodFor(r)}`;

  const showOptions: { key: Show; label: string; n: number }[] = [
    { key: 'all', label: 'All', n: all.length },
    { key: 'todo', label: 'To count', n: all.length - done },
    { key: 'done', label: 'Counted', n: done },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Counts</div>
          <div className="page-subtitle">
            Week ending {formatLongDate(week)} · {isOpen ? 'open, due end of day Wednesday' : 'closed'}
          </div>
        </div>
      </div>

      <div className="filter-row">
        <WeekSelect value={week} onChange={setWeek} />
        <RegionSelect allowed={locationsFor('counts', 'view')} value={region} onChange={setRegion} />
        <input
          className="control"
          style={{ width: 200 }}
          placeholder="Find a location"
          aria-label="Find a location"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {counts.isError && <ErrorBanner message={(counts.error as Error).message} />}
      {counts.isLoading && <div className="spinner-note">Loading the week…</div>}

      {counts.data && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px 24px', flexWrap: 'wrap', margin: '4px 0 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 320px', maxWidth: 520 }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                <strong style={{ color: 'var(--text)', fontSize: 15 }}>{done}</strong> of {all.length} counted
              </span>
              <div className="bar-track" style={{ height: 6 }}>
                <div className="bar-fill" style={{ width: `${all.length ? (done / all.length) * 100 : 0}%`, background: 'var(--green)' }} />
              </div>
            </div>
            <div className="segmented" role="tablist" style={{ marginLeft: 'auto' }}>
              {showOptions.map((o) => (
                <button
                  key={o.key}
                  role="tab"
                  aria-selected={show === o.key}
                  className={`segmented__item${show === o.key ? ' segmented__item--active' : ''}`}
                  onClick={() => setShow(o.key)}
                >
                  {o.label} <span style={{ fontWeight: 500, color: 'var(--muted)' }}>{o.n}</span>
                </button>
              ))}
            </div>
          </div>

          {groups.length === 0 ? (
            <div className="card meta">
              {all.length === 0
                ? 'No active locations in your regions.'
                : show === 'todo' && !search ? 'Every location has counted this week.' : 'No locations match.'}
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <SortableHeader label="LOCATION" column="location" sort={sort} onSort={onSort} />
                    <SortableHeader label="REGION" column="region" sort={sort} onSort={onSort} />
                    <SortableHeader label="TYPE" column="type" sort={sort} onSort={onSort} />
                    <SortableHeader label="THIS WEEK" column="status" sort={sort} onSort={onSort} />
                    <th aria-label="Action" />
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <Fragment key={g.name}>
                      <tr>
                        <td colSpan={5} style={{ background: 'var(--surface-muted)', padding: '8px 14px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, color: 'var(--text-secondary)' }}>
                              {g.name.toUpperCase()}
                            </span>
                            <span className="meta">{g.done} of {g.total} counted</span>
                          </div>
                        </td>
                      </tr>
                      {g.rows.map((r) => (
                        <tr key={r.locationId} className="clickable" onClick={() => navigate(toCount(r))}>
                          <td className="strong" style={{ whiteSpace: 'nowrap' }}>{r.code}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{regionName(r.regionCode)}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{r.type}</td>
                          <td style={{ whiteSpace: 'nowrap' }}><WeekStatus row={r} isOpen={isOpen} /></td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap', width: 1 }}>
                            <Link
                              to={toCount(r)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
                            >
                              {!r.canEnter ? 'View' : r.status === 'missing' ? 'Enter count' : 'Open'} →
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** A dot in the status's colour, then what happened: when and by whom, or that it hasn't. */
function WeekStatus({ row, isOpen }: { row: WeekCountRow; isOpen: boolean }) {
  const dot = (color: string) => (
    <span
      aria-hidden
      style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: color, marginRight: 8, verticalAlign: 1 }}
    />
  );

  if (row.status === 'missing' || !row.latest) {
    return isOpen
      ? <span style={{ color: 'var(--muted)' }}>{dot('var(--input-border)')}Not counted yet</span>
      : <span style={{ color: 'var(--red)', fontWeight: 600 }}>{dot('var(--red)')}Missing</span>;
  }

  const color = row.status === 'late' ? 'var(--orange)' : row.status === 'imported' ? 'var(--muted)' : 'var(--green)';
  // An imported count has no person to name, so it shows only its date.
  return (
    <span>
      {dot(color)}
      <span style={{ color: 'var(--text)' }}>{formatDate(row.latest.businessDate)}</span>
      {row.latest.source === 'app' && <span style={{ color: 'var(--muted)' }}> · {row.latest.by}</span>}
      {row.status === 'late' && <span style={{ color: 'var(--orange)', fontWeight: 600 }}> · late</span>}
    </span>
  );
}
